import { Fragment, useEffect, useState } from "react";
import { Switch, Listbox, Transition } from "@headlessui/react";
import {
  Link,
  useParams,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MailIcon,
  OfficeBuildingIcon,
  LocationMarkerIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  BellIcon,
  SearchIcon,
  ArrowLeftIcon,
} from "@heroicons/react/outline";
import ReactTimeAgo from "react-time-ago";
import { toast } from "react-toastify";

import AnimatedPage from "../../components/animatedPage/AnimatedPage";
import Loader from "../../components/loader/Loader";

import { useAppSelector } from "../../app/hooks";
import { selectUser } from "../userProfile/userSlice";

import * as api from "./apiService";
import SchedulerAccessSettings from '../scheduler/components/SchedulerAccessSettings';
import BadgeAccessSettings from '../airportBadges/BadgeAccessSettings';

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const EmptyState = ({ title, description }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center">
    <div className="text-sm font-semibold text-slate-800">{title}</div>
    <div className="mt-1 text-sm text-slate-500">{description}</div>
  </div>
);

const SectionCard = ({
  title,
  description,
  icon,
  open,
  onToggle,
  children,
  badge,
}) => (
  <div className="rounded-[28px] border border-slate-200 bg-white shadow-sm overflow-visible">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-start justify-between gap-4 px-6 py-5 text-left transition hover:bg-slate-50/80"
    >
      <div className="flex min-w-0 items-start gap-4">
        <div className="rounded-2xl bg-slate-100 p-3 text-slate-600">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            {badge ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                {badge}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
      </div>

      <div className="pt-1 text-slate-400">
        {open ? (
          <ChevronUpIcon className="h-5 w-5" />
        ) : (
          <ChevronDownIcon className="h-5 w-5" />
        )}
      </div>
    </button>

    {open ? (
      <div className="border-t border-slate-200 px-6 py-6">{children}</div>
    ) : null}
  </div>
);

const ToggleRow = ({
  label,
  description,
  enabled,
  onChange,
  border = true,
}) => (
  <div
    className={classNames(
      "flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between",
      border ? "border-b border-slate-200 last:border-b-0" : "",
    )}
  >
    <div className="max-w-3xl pr-4">
      <div className="text-sm font-semibold text-slate-900">{label}</div>
      {description ? (
        <div className="mt-1 text-sm text-slate-500">{description}</div>
      ) : null}
    </div>

    <Switch
      checked={!!enabled}
      onChange={onChange}
      className={classNames(
        enabled ? "bg-red-600" : "bg-slate-300",
        "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2",
      )}
    >
      <span
        aria-hidden="true"
        className={classNames(
          enabled ? "translate-x-5" : "translate-x-0",
          "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
        )}
      />
    </Switch>
  </div>
);

const SearchableListbox = ({
  value,
  onChange,
  options,
  placeholder,
  searchValue,
  onSearchChange,
}) => {
  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className="relative z-50">
          <Listbox.Button className="relative w-full rounded-xl border border-slate-300 bg-white py-3 pl-4 pr-10 text-left text-sm text-slate-900 shadow-sm transition focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20">
            <span className="block truncate">{value?.name || placeholder}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronDownIcon className="h-5 w-5 text-slate-400" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute left-0 right-0 z-[9999] mt-2 max-h-72 overflow-auto rounded-2xl border border-slate-200 bg-white py-2 shadow-2xl focus:outline-none">
              <div className="sticky top-0 z-10 bg-white px-2 pb-2">
                <div className="relative">
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder={`Search ${placeholder.toLowerCase()}...`}
                    className="w-full rounded-xl border border-slate-300 bg-slate-50 py-2.5 pl-3 pr-10 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                  />
                  <SearchIcon className="pointer-events-none absolute right-3 top-2.5 h-5 w-5 text-slate-400" />
                </div>
              </div>

              {options.map((option) => (
                <Listbox.Option
                  key={`${placeholder}-${option.id}-${option.name}`}
                  value={option}
                  className={({ active }) =>
                    classNames(
                      active ? "bg-red-600 text-white" : "text-slate-900",
                      "relative cursor-pointer select-none py-2.5 pl-4 pr-10 text-sm",
                    )
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={classNames(
                          selected ? "font-semibold" : "font-normal",
                          "block truncate",
                        )}
                      >
                        {option.name}
                      </span>
                      {selected ? (
                        <span
                          className={classNames(
                            active ? "text-white" : "text-red-600",
                            "absolute inset-y-0 right-0 flex items-center pr-3",
                          )}
                        >
                          <CheckIcon className="h-5 w-5" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};

const RoleBadge = ({ userDetails }) => {
  let label = "User";
  let classes = "bg-slate-100 text-slate-700 ring-slate-200";

  if (userDetails.is_superuser || userDetails.is_staff) {
    label = "Admin";
    classes = "bg-green-100 text-green-700 ring-green-200";
  } else if (userDetails.is_account_manager) {
    label = "Account Manager";
    classes = "bg-blue-100 text-blue-700 ring-blue-200";
  } else if (userDetails.is_project_manager) {
    label = "Project Manager";
    classes = "bg-violet-100 text-violet-700 ring-violet-200";
  } else if (userDetails.is_internal_coordinator) {
    label = "Coordinator";
    classes = "bg-orange-100 text-orange-700 ring-orange-200";
  } else if (userDetails.customer_name) {
    label = "Customer";
    classes = "bg-sky-100 text-sky-700 ring-sky-200";
  }

  return (
    <span
      className={classNames(
        "inline-flex rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset",
        classes,
      )}
    >
      {label}
    </span>
  );
};

const defaultPickerValue = { id: null, name: "Select an option" };

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAppSelector(selectUser);

  const [userDetails, setUserDetails] = useState({});
  const [loading, setLoading] = useState(true);

  const [airports, setAirports] = useState([]);
  const [totalAirports, setTotalAirports] = useState(0);
  const [loadingAirports, setLoadingAirports] = useState(false);
  const [airportSearchText, setAirportSearchText] = useState("");
  const [airportAlreadyAdded, setAirportAlreadyAdded] = useState(false);
  const [availableAirports, setAvailableAirports] = useState([]);
  const [airportSelected, setAirportSelected] = useState(defaultPickerValue);

  const [customers, setCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [customerAlreadyAdded, setCustomerAlreadyAdded] = useState(false);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [customerSelected, setCustomerSelected] = useState(defaultPickerValue);

  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locations, setLocations] = useState([]);
  const [totalLocations, setTotalLocations] = useState(0);
  const [locationSearchText, setLocationSearchText] = useState("");
  const [locationAlreadyAdded, setLocationAlreadyAdded] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [locationSelected, setLocationSelected] = useState(defaultPickerValue);

  const [defaultEmail, setDefaultEmail] = useState("");
  const [additionalEmails, setAdditionalEmails] = useState([]);

  const [additionalEmailOpen, setAdditionalEmailOpen] = useState(false);
  const [newAdditionalEmail, setNewAdditionalEmail] = useState("");

  const [showEmailSection, setShowEmailSection] = useState(false);
  const [showLocationSection, setShowLocationSection] = useState(false);
  const [showAirportSection, setShowAirportSection] = useState(false);
  const [showCustomerSection, setShowCustomerSection] = useState(false);
  const [showEmailNotificationSection, setShowEmailNotificationSection] =
    useState(false);
  const [showSMSNotificationSection, setShowSMSNotificationSection] =
    useState(false);
  const [showPermissionSection, setShowPermissionSection] = useState(false);

  const profile = userDetails.profile || {};

  useEffect(() => {
    let timeoutID = setTimeout(() => {
      getAirports();
    }, 500);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [airportSearchText]);

  useEffect(() => {
    let timeoutID = setTimeout(() => {
      getCustomers();
    }, 500);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [customerSearchText]);

  useEffect(() => {
    let timeoutID = setTimeout(() => {
      getLocations();
    }, 500);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [locationSearchText]);

  useEffect(() => {
    getUserDetails();
    getAvailableAirports();
    getUserCustomers();
    getLocationsForUser();
    setAirportAlreadyAdded(false);
    setLocationAlreadyAdded(false);
  }, [userId]);

  const getAirports = async () => {
    setLoadingAirports(true);
    try {
      const request = {
        name: airportSearchText,
      };

      const { data } = await api.getAirports(request);

      setAirports(data.results);
      setTotalAirports(data.count);
      setAirportAlreadyAdded(false);
    } catch (error) {
      toast.error("Unable to get airports");
    }
    setLoadingAirports(false);
  };

  const getCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const request = {
        name: customerSearchText,
      };

      const { data } = await api.getCustomers(request);

      setCustomers(data.results);
      setTotalCustomers(data.count);
      setCustomerAlreadyAdded(false);
    } catch (error) {
      toast.error("Unable to get customers");
    }

    setLoadingCustomers(false);
  };

  const getLocations = async () => {
    setLoadingLocations(true);

    const request = {
      name: locationSearchText,
    };

    try {
      const { data } = await api.getLocations(request);

      setLocations(data.results);
      setTotalLocations(data.count);
      setLocationAlreadyAdded(false);
    } catch (error) {
      toast.error("Unable to get locations");
    }

    setLoadingLocations(false);
  };

  const getUserDetails = async () => {
    setLoading(true);

    try {
      const { data } = await api.getUserDetails(userId);

      setUserDetails(data);
      setAdditionalEmails(data.additional_emails);
      setDefaultEmail(data.email);
      setLoading(false);
    } catch (err) {
      toast.error("Unable to get user details");
    }
  };

  const getAvailableAirports = async () => {
    try {
      const { data } = await api.getUserAvailableAirports(userId);

      setAvailableAirports(data);
    } catch (err) {
      toast.error("Unable to get airports for user");
    }
  };

  const getUserCustomers = async () => {
    try {
      const { data } = await api.getUserCustomers(userId);

      setAvailableCustomers(data);
    } catch (err) {
      toast.error("Unable to get customers for user");
    }
  };

  const getLocationsForUser = async () => {
    try {
      const { data } = await api.getLocationsForUser(userId);

      setAvailableLocations(data);
    } catch (err) {
      toast.error("Unable to get locations for user");
    }
  };

  const addAvailableAirport = async (airportId) => {
    const request = {
      user_id: userId,
      airport_id: airportId,
      action: "add",
    };

    try {
      const airportExists = availableAirports.find(
        (airport) => airport.id === airportId,
      );

      if (airportExists) {
        setAirportAlreadyAdded(true);
      } else {
        const { data } = await api.updateUserAvailableAirport(request);
        setAirportAlreadyAdded(false);
        setAvailableAirports([...availableAirports, data]);
        toast.success("Airport added");
      }
    } catch (error) {
      setAirportAlreadyAdded(false);
    }
  };

  const addUserCustomer = async (customerId) => {
    const request = {
      user_id: userId,
      customer_id: customerId,
      action: "add",
    };

    try {
      const customerExists = availableCustomers.find(
        (customer) => customer.id === customerId,
      );

      if (customerExists) {
        setCustomerAlreadyAdded(true);
      } else {
        const { data } = await api.updateUserCustomer(request);
        setCustomerAlreadyAdded(false);
        setAvailableCustomers([...availableCustomers, data]);

        toast.success("Customer added");
      }
    } catch (error) {
      setCustomerAlreadyAdded(false);
    }
  };

  const addAvailableLocation = async (locationId) => {
    const request = {
      user_id: userId,
      location_id: locationId,
      action: "add",
    };

    try {
      const locationExists = availableLocations.find(
        (location) => location.id === locationId,
      );

      if (locationExists) {
        setLocationAlreadyAdded(true);
      } else {
        const { data } = await api.updateUserAvailableLocation(userId, request);
        setLocationAlreadyAdded(false);
        setAvailableLocations([...availableLocations, data]);

        toast.success("Location added");
      }
    } catch (err) {
      toast.error("Unable to add location");
    }
  };

  const deleteAvailableAirport = async (airportId) => {
    try {
      const request = {
        user_id: userId,
        airport_id: airportId,
        action: "delete",
      };

      await api.updateUserAvailableAirport(request);

      setAirportAlreadyAdded(false);

      setAvailableAirports(
        availableAirports.filter((airport) => airport.id !== airportId),
      );

      toast.success("Airport removed");
    } catch (error) {
      toast.error("Unable to remove airport");
    }
  };

  const deleteUserCustomer = async (customerId) => {
    try {
      const request = {
        user_id: userId,
        customer_id: customerId,
        action: "delete",
      };

      await api.updateUserCustomer(request);

      setCustomerAlreadyAdded(false);

      setAvailableCustomers(
        availableCustomers.filter((customer) => customer.id !== customerId),
      );

      toast.success("Customer removed");
    } catch (error) {
      toast.error("Unable to remove customer");
    }
  };

  const deleteAvailableLocation = async (locationId) => {
    try {
      const request = {
        user_id: userId,
        location_id: locationId,
        action: "delete",
      };

      await api.updateUserAvailableLocation(userId, request);

      setLocationAlreadyAdded(false);

      setAvailableLocations(
        availableLocations.filter((location) => location.id !== locationId),
      );

      toast.success("Location removed");
    } catch (err) {
      toast.error("Unable to remove location");
    }
  };

  const saveDefaultEmail = async () => {
    try {
      const request = {
        email: defaultEmail,
      };

      await api.updateUser(userId, request);

      toast.success("Default email saved");
    } catch (err) {
      toast.error("Unable to save default email");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      getAirports();
    }
  };

  const handleKeyDownCustomers = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      getCustomers();
    }
  };

  const handleKeyDownLocations = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      getLocations();
    }
  };

  const handleAdditionalEmailOpen = () => {
    setAdditionalEmailOpen(true);
    setNewAdditionalEmail("");
  };

  const handleAddAdditionalEmail = async () => {
    try {
      const request = {
        new_additional_email: newAdditionalEmail,
        user_id: userId,
      };

      const { data } = await api.addNewAdditionalEmail(request);

      setAdditionalEmails([...additionalEmails, data]);
      setAdditionalEmailOpen(false);

      toast.success("Additional email added");
    } catch (err) {
      toast.error("Email already exists");
    }
  };

  const updateAdditionalEmail = async (additionalEmail) => {
    try {
      const request = {
        user_id: userId,
        email: additionalEmail.email,
      };

      await api.updateAdditionalEmail(additionalEmail.id, request);

      toast.success("Additional email updated");
    } catch (err) {
      toast.error("Unable to update additional email");
    }
  };

  const deleteAdditionalEmail = async (userEmailId) => {
    try {
      await api.deleteAdditionalEmail(userEmailId);

      setAdditionalEmails(
        additionalEmails.filter((email) => email.id !== userEmailId),
      );

      toast.success("Additional email deleted");
    } catch (err) {
      toast.error("Unable to delete additional email");
    }
  };

  const handleToggleEmailNotifications = async () => {
    try {
      const request = {
        email_notifications: !userDetails.profile.email_notifications,
      };

      await api.updateUser(userId, request);

      if (!request.email_notifications) {
        setUserDetails({
          ...userDetails,
          profile: {
            ...userDetails.profile,
            email_notifications: !userDetails.profile.email_notifications,
            enable_email_notification_job_created: false,
            enable_email_notification_scheduled_job_created: false,
            enable_email_notification_job_confirmed: false,
            enable_email_notification_job_accepted: false,
            enable_email_notification_job_returned: false,
            enable_email_notification_job_comment_added: false,
            enable_email_notification_job_completed: false,
          },
        });
      } else {
        setUserDetails({
          ...userDetails,
          profile: {
            ...userDetails.profile,
            email_notifications: !userDetails.profile.email_notifications,
          },
        });
      }
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleEmailNotificationJobCreated = async () => {
    try {
      const request = {
        enable_email_notification_job_created:
          !userDetails.profile.enable_email_notification_job_created,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_email_notification_job_created:
            !userDetails.profile.enable_email_notification_job_created,
        },
      });
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleEmailNotificationScheduledJobCreated = async () => {
    try {
      const request = {
        enable_email_notification_scheduled_job_created:
          !userDetails.profile.enable_email_notification_scheduled_job_created,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_email_notification_scheduled_job_created:
            !userDetails.profile
              .enable_email_notification_scheduled_job_created,
        },
      });
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleEmailNotificationJobConfirmed = async () => {
    try {
      const request = {
        enable_email_notification_job_confirmed:
          !userDetails.profile.enable_email_notification_job_confirmed,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_email_notification_job_confirmed:
            !userDetails.profile.enable_email_notification_job_confirmed,
        },
      });
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleEmailNotificationJobAccepted = async () => {
    try {
      const request = {
        enable_email_notification_job_accepted:
          !userDetails.profile.enable_email_notification_job_accepted,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_email_notification_job_accepted:
            !userDetails.profile.enable_email_notification_job_accepted,
        },
      });
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleEmailNotificationJobReturned = async () => {
    try {
      const request = {
        enable_email_notification_job_returned:
          !userDetails.profile.enable_email_notification_job_returned,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_email_notification_job_returned:
            !userDetails.profile.enable_email_notification_job_returned,
        },
      });
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleEmailNotificationJobCommentAdded = async () => {
    try {
      const request = {
        enable_email_notification_job_comment_added:
          !userDetails.profile.enable_email_notification_job_comment_added,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_email_notification_job_comment_added:
            !userDetails.profile.enable_email_notification_job_comment_added,
        },
      });
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleEmailNotificationJobCompleted = async () => {
    try {
      const request = {
        enable_email_notification_job_completed:
          !userDetails.profile.enable_email_notification_job_completed,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_email_notification_job_completed:
            !userDetails.profile.enable_email_notification_job_completed,
        },
      });
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleSMSNotifications = async () => {
    try {
      const request = {
        sms_notifications: !userDetails.profile.sms_notifications,
      };

      await api.updateUser(userId, request);

      if (!request.sms_notifications) {
        setUserDetails({
          ...userDetails,
          profile: {
            ...userDetails.profile,
            sms_notifications: !userDetails.profile.sms_notifications,
            enable_sms_notification_job_created: false,
            enable_sms_notification_job_started: false,
            enable_sms_notification_job_completed: false,
            enable_sms_notification_job_cancelled: false,
          },
        });
      } else {
        setUserDetails({
          ...userDetails,
          profile: {
            ...userDetails.profile,
            sms_notifications: !userDetails.profile.sms_notifications,
          },
        });
      }
    } catch (err) {
      toast.error("Unable to update sms notifications");
    }
  };

  const handleToggleSMSNotificationJobCreated = async () => {
    try {
      const request = {
        enable_sms_notification_job_created:
          !userDetails.profile.enable_sms_notification_job_created,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_sms_notification_job_created:
            !userDetails.profile.enable_sms_notification_job_created,
        },
      });
    } catch (err) {
      toast.error("Unable to update sms notifications");
    }
  };

  const handleToggleSMSNotificationJobStarted = async () => {
    try {
      const request = {
        enable_sms_notification_job_started:
          !userDetails.profile.enable_sms_notification_job_started,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_sms_notification_job_started:
            !userDetails.profile.enable_sms_notification_job_started,
        },
      });
    } catch (err) {
      toast.error("Unable to update sms notifications");
    }
  };

  const handleToggleSMSNotificationJobCompleted = async () => {
    try {
      const request = {
        enable_sms_notification_job_completed:
          !userDetails.profile.enable_sms_notification_job_completed,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_sms_notification_job_completed:
            !userDetails.profile.enable_sms_notification_job_completed,
        },
      });
    } catch (err) {
      toast.error("Unable to update sms notifications");
    }
  };

  const handleToggleSMSNotificationJobCancelled = async () => {
    try {
      const request = {
        enable_sms_notification_job_cancelled:
          !userDetails.profile.enable_sms_notification_job_cancelled,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_sms_notification_job_cancelled:
            !userDetails.profile.enable_sms_notification_job_cancelled,
        },
      });
    } catch (err) {
      toast.error("Unable to update sms notifications");
    }
  };

  const handleToggleEmailNotificationInventoryOutOfStock = async () => {
    try {
      const request = {
        enable_email_notification_inventory_out_of_stock:
          !userDetails.profile.enable_email_notification_inventory_out_of_stock,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_email_notification_inventory_out_of_stock:
            !userDetails.profile
              .enable_email_notification_inventory_out_of_stock,
        },
      });
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleEmailInventoryThresholdMet = async () => {
    try {
      const request = {
        enable_email_notification_inventory_threshold_met:
          !userDetails.profile
            .enable_email_notification_inventory_threshold_met,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_email_notification_inventory_threshold_met:
            !userDetails.profile
              .enable_email_notification_inventory_threshold_met,
        },
      });
    } catch (err) {
      toast.error("Unable to update email notifications");
    }
  };

  const handleToggleShowAirportFees = async () => {
    try {
      const request = {
        show_airport_fees: !userDetails.profile.show_airport_fees,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          show_airport_fees: !userDetails.profile.show_airport_fees,
        },
      });
    } catch (err) {
      toast.error("Unable to update show airport fees");
    }
  };

  const handleToggleShowJobPrice = async () => {
    try {
      const request = {
        show_job_price: !userDetails.profile.show_job_price,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          show_job_price: !userDetails.profile.show_job_price,
        },
      });
    } catch (err) {
      toast.error("Unable to update show job price");
    }
  };

  const handleToggleEnableAllCustomers = async () => {
    try {
      const request = {
        enable_all_customers: !userDetails.profile.enable_all_customers,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_all_customers: !userDetails.profile.enable_all_customers,
        },
      });
    } catch (err) {
      toast.error("Unable to update enable all customers");
    }
  };

  const handleToggleEnableAllAirports = async () => {
    try {
      const request = {
        enable_all_airports: !userDetails.profile.enable_all_airports,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_all_airports: !userDetails.profile.enable_all_airports,
        },
      });
    } catch (err) {
      toast.error("Unable to update enable all airports");
    }
  };

  const handleToggleEnableInventoryDashboard = async () => {
    try {
      const request = {
        enable_inventory_dashboard:
          !userDetails.profile.enable_inventory_dashboard,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_inventory_dashboard:
            !userDetails.profile.enable_inventory_dashboard,
        },
      });
    } catch (err) {
      toast.error("Unable to update enable inventory dashboard");
    }
  };

  const handleToggleEnableEstimates = async () => {
    try {
      const request = {
        enable_estimates: !userDetails.profile.enable_estimates,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_estimates: !userDetails.profile.enable_estimates,
        },
      });
    } catch (err) {
      toast.error("Unable to update enable estimates");
    }
  };

  const handleToggleIsJobSubmitterOnly = async () => {
    try {
      const request = {
        is_job_submitter_only: !userDetails.profile.is_job_submitter_only,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          is_job_submitter_only: !userDetails.profile.is_job_submitter_only,
        },
      });
    } catch (err) {
      toast.error("Unable to update flag");
    }
  };

  const handleToggleEnableAcceptJobs = async () => {
    try {
      const request = {
        enable_accept_jobs: !userDetails.profile.enable_accept_jobs,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_accept_jobs: !userDetails.profile.enable_accept_jobs,
        },
      });
    } catch (err) {
      toast.error("Unable to update enable accept jobs");
    }
  };

  const handleToggleMasterVendorPm = async () => {
    try {
      const request = {
        master_vendor_pm: !userDetails.profile.master_vendor_pm,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          master_vendor_pm: !userDetails.profile.master_vendor_pm,
        },
      });
    } catch (err) {
      toast.error("Unable to update master vendor pm");
    }
  };

  const handleToggleEnableConfirmJobs = async () => {
    try {
      const request = {
        enable_confirm_jobs: !userDetails.profile.enable_confirm_jobs,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_confirm_jobs: !userDetails.profile.enable_confirm_jobs,
        },
      });
    } catch (err) {
      toast.error("Unable to update enable confirm jobs");
    }
  };

  const handleTogglePromptRequestedBy = async () => {
    try {
      const request = {
        prompt_requested_by: !userDetails.profile.prompt_requested_by,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          prompt_requested_by: !userDetails.profile.prompt_requested_by,
        },
      });
    } catch (err) {
      toast.error("Unable to update prompt requested by");
    }
  };

  const handleToggleEnableInvoice = async () => {
    try {
      const request = {
        enable_invoice: !userDetails.profile.enable_invoice,
      };

      await api.updateUser(userId, request);

      setUserDetails({
        ...userDetails,
        profile: {
          ...userDetails.profile,
          enable_invoice: !userDetails.profile.enable_invoice,
        },
      });
    } catch (err) {
      toast.error("Unable to update enable invoice");
    }
  };

  if (loading) {
    return (
      <AnimatedPage>
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <Loader />
        </div>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage>
      <div className="mx-auto max-w-7xl px-4 pb-14 pt-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => navigate("/users")}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Users
          </button>
        </div>

        <div className="overflow-hidden rounded-[32px] border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 shadow-xl">
          <div className="px-6 py-8 sm:px-8">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-center gap-5">
                {profile?.avatar ? (
                  <img
                    src={profile.avatar}
                    alt=""
                    className="h-24 w-24 rounded-3xl object-cover ring-4 ring-white/10 sm:h-28 sm:w-28"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-3xl bg-white/10 text-slate-200 sm:h-28 sm:w-28">
                    <UserCircleIcon className="h-16 w-16" />
                  </div>
                )}

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-3xl font-semibold tracking-tight text-white">
                      {userDetails.first_name} {userDetails.last_name}
                    </h1>
                    <RoleBadge userDetails={userDetails} />
                  </div>

                  <div className="mt-2 text-sm text-slate-300">
                    {userDetails.email || "No email specified"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6">
          <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">
              General Information
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Overview of the user profile and access context.
            </p>

            <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  First Name
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-900 break-words">
                  {userDetails.first_name || "Not specified"}
                </dd>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Last Name
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-900 break-words">
                  {userDetails.last_name || "Not specified"}
                </dd>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Username
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-900 break-words">
                  {userDetails.username || "Not specified"}
                </dd>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Location
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-900 break-words">
                  {userDetails.location || "Not specified"}
                </dd>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Customer
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-900 break-words">
                  {userDetails.customer_name || "Not specified"}
                </dd>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Member Since
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-900 break-words">
                  {userDetails.member_since ? (
                    <ReactTimeAgo
                      date={new Date(userDetails.member_since)}
                      locale="en-US"
                      timeStyle="twitter"
                    />
                  ) : (
                    "Not specified"
                  )}
                </dd>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 min-w-0 sm:col-span-2 xl:col-span-1">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Phone
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-900 break-words">
                  {userDetails.phone_number || "Not specified"}
                </dd>
              </div>
            </dl>
          </div>

          <div className="space-y-6">
            <SectionCard
              title="Emails"
              description="Configure default and additional notification email addresses."
              icon={<MailIcon className="h-6 w-6" />}
              open={showEmailSection}
              onToggle={() => setShowEmailSection(!showEmailSection)}
              badge={`${1 + additionalEmails.length} total`}
            >
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Default Email
                  </label>
                  <input
                    type="email"
                    value={defaultEmail}
                    onChange={(e) => setDefaultEmail(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder="you@example.com"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={saveDefaultEmail}
                    className="w-full rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 lg:w-auto"
                  >
                    Save Default Email
                  </button>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    Additional Emails
                  </h3>
                  <p className="text-sm text-slate-500">
                    Customers receive notifications to all emails listed here.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleAdditionalEmailOpen}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  Add Email
                </button>
              </div>

              {additionalEmailOpen ? (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto_auto]">
                    <input
                      type="email"
                      value={newAdditionalEmail}
                      onChange={(e) => setNewAdditionalEmail(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                      placeholder="you@example.com"
                    />
                    <button
                      type="button"
                      onClick={() => setAdditionalEmailOpen(false)}
                      className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddAdditionalEmail}
                      className="rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="mt-5 space-y-4">
                {additionalEmails.length === 0 ? (
                  <EmptyState
                    title="No additional emails"
                    description="Add one or more extra email addresses for this user."
                  />
                ) : (
                  additionalEmails.map((additionalEmail) => (
                    <div
                      key={additionalEmail.id}
                      className="rounded-2xl border border-slate-200 bg-white p-4"
                    >
                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto_auto]">
                        <input
                          type="email"
                          value={additionalEmail.email}
                          onChange={(event) =>
                            setAdditionalEmails(
                              additionalEmails.map((email) =>
                                email.id === additionalEmail.id
                                  ? { ...email, email: event.target.value }
                                  : email,
                              ),
                            )
                          }
                          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                          placeholder="you@example.com"
                        />
                        <button
                          type="button"
                          onClick={() => updateAdditionalEmail(additionalEmail)}
                          className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            deleteAdditionalEmail(additionalEmail.id)
                          }
                          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 shadow-sm transition hover:bg-red-100"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </SectionCard>

            <SectionCard
              title="Permissions"
              description="Configure which features and capabilities this user can access."
              icon={<ShieldCheckIcon className="h-6 w-6" />}
              open={showPermissionSection}
              onToggle={() => setShowPermissionSection(!showPermissionSection)}
            >
              <BadgeAccessSettings user={userDetails} currentUser={currentUser} onSaved={updated => setUserDetails(previous => ({ ...previous, profile: updated.profile, access: updated.access }))} />
              <SchedulerAccessSettings user={userDetails} currentUser={currentUser} onSaved={updated => setUserDetails(previous => ({ ...previous, profile: updated.profile, access: updated.access }))} />
              <div>
                {userDetails?.is_internal_coordinator && (
                  <>
                    <ToggleRow
                      label="Show Airport Fees"
                      description="Allows the user to see airport and FBO fees while creating or reviewing jobs. This is useful when the user needs to understand the full operational cost before the job is submitted or priced. Example: if a job is being created at MIA and the airport or FBO has an added fee, the coordinator will be able to see that charge during the job setup process."
                      enabled={userDetails.profile.show_airport_fees}
                      onChange={handleToggleShowAirportFees}
                    />
                    <ToggleRow
                      label="Show Job Price"
                      description="Allows the user to see the price of a job in the job details view. This should be enabled only for users who are allowed to view financial information. Example: an internal coordinator reviewing a submitted job can see the quoted price before discussing it with the customer or vendor."
                      enabled={userDetails.profile.show_job_price}
                      onChange={handleToggleShowJobPrice}
                    />
                    <ToggleRow
                      label="Enable All Customers"
                      description="Gives the user access to all customers in the system instead of limiting them to only assigned customers. This is useful for users who need broad visibility across accounts. Example: a senior coordinator covering multiple teams can search and create jobs for any customer, even if that customer was not manually assigned to them."
                      enabled={userDetails.profile.enable_all_customers}
                      onChange={handleToggleEnableAllCustomers}
                    />
                    <ToggleRow
                      label="Enable All Airports"
                      description="Gives the user access to all airports in the system instead of limiting them to only assigned airports. This is helpful for users who need to support jobs across the full network. Example: a coordinator helping with jobs nationwide can select any airport when creating or managing a job, not just the airports specifically assigned to their profile."
                      enabled={userDetails.profile.enable_all_airports}
                      onChange={handleToggleEnableAllAirports}
                    />
                    <ToggleRow
                      label="Enable Inventory Dashboard"
                      description="Allows the user to access the inventory dashboard and work with inventory-related data. This should be enabled for users involved in stock monitoring or supply management. Example: a coordinator responsible for shop supplies can open the inventory dashboard to review what products are running low at a location."
                      enabled={userDetails.profile.enable_inventory_dashboard}
                      onChange={handleToggleEnableInventoryDashboard}
                    />
                    <ToggleRow
                      label="Enable Estimates"
                      description="Allows the user to view and manage job estimates. This is useful for users involved in pricing or pre-approval workflows. Example: a coordinator preparing a quote for a customer can create or review an estimate before the job is finalized."
                      enabled={userDetails.profile.enable_estimates}
                      onChange={handleToggleEnableEstimates}
                      border={false}
                    />
                  </>
                )}

                {userDetails?.is_project_manager && (
                  <>
                    <ToggleRow
                      label="Enable Accept Jobs"
                      description="Allows the project manager to formally accept assigned jobs before starting work. If this is turned off, the project manager skips the acceptance step and moves directly toward starting the job. Example: if your vendor workflow requires PMs to confirm they received the assignment before arriving on site, keep this enabled."
                      enabled={userDetails.profile.enable_accept_jobs}
                      onChange={handleToggleEnableAcceptJobs}
                    />
                    <ToggleRow
                      label="Master Vendor PM"
                      description="Gives this project manager broader visibility and control across the vendor’s jobs, not just the jobs personally assigned to them. This is intended for lead PMs or supervisors at the vendor level. Example: a vendor team lead can see jobs assigned to other PMs in the same vendor, review completed services across the vendor, and reassign jobs when someone is unavailable."
                      enabled={userDetails.profile.master_vendor_pm}
                      onChange={handleToggleMasterVendorPm}
                      border={!userDetails?.is_external_project_manager}
                    />

                    {userDetails?.is_external_project_manager && (
                      <ToggleRow
                        label="Show Job Price"
                        description="Allows the external project manager to see the price of a job in the job details view. This should only be enabled if that vendor-facing user is allowed to view pricing. Example: a trusted external PM managing premium jobs may need to see the job price before discussing scope, staffing, or special requests with their team."
                        enabled={userDetails.profile.show_job_price}
                        onChange={handleToggleShowJobPrice}
                        border={false}
                      />
                    )}
                  </>
                )}

                {userDetails?.is_customer_user && (
                  <>
                    <ToggleRow
                      label="Enable Confirm Jobs"
                      description="Allows the customer user to confirm jobs as part of the approval workflow. If disabled, that customer user can still view jobs but cannot complete the confirmation step. Example: a dispatcher at the customer account can review a submitted job and confirm it for service without waiting for an internal admin."
                      enabled={userDetails.profile.enable_confirm_jobs}
                      onChange={handleToggleEnableConfirmJobs}
                    />
                    <ToggleRow
                      label="Show Job Price"
                      description="Allows the customer user to see the price of a job in the job details view. This should be enabled only when the customer contact is allowed to review pricing. Example: a billing contact at the customer account can open the job and immediately see the quoted amount before approving the work."
                      enabled={userDetails.profile.show_job_price}
                      onChange={handleToggleShowJobPrice}
                    />
                    <ToggleRow
                      label="Prompt Requested By"
                      description="Prompts the customer user to enter the name of the person requesting the service when creating a job. This is especially helpful for shared or generic customer logins. Example: if several people at the same company use one customer account, the system can still record whether the request came from John in flight ops or Sarah in scheduling."
                      enabled={userDetails.profile.prompt_requested_by}
                      onChange={handleTogglePromptRequestedBy}
                    />
                    <ToggleRow
                      label="Job Submitter Only"
                      description="Limits the user to job submission-related functionality and hides broader account features such as dashboards or advanced management views. This is useful for simple requester accounts. Example: a front-desk employee who only needs to submit cleaning requests can create jobs without seeing admin-style screens or account-wide operational tools."
                      enabled={userDetails.profile.is_job_submitter_only}
                      onChange={handleToggleIsJobSubmitterOnly}
                      border={false}
                    />
                  </>
                )}

                {(userDetails?.is_staff ||
                  userDetails?.is_superuser ||
                  userDetails?.is_account_manager) && (
                  <>
                    <ToggleRow
                      label="Enable Estimates"
                      description="Allows the user to view and manage estimates for jobs before they are finalized. This is useful for roles involved in quoting, pre-approval, or pricing oversight. Example: an account manager preparing a quote for a recurring customer can generate and adjust an estimate before the job is approved."
                      enabled={userDetails.profile.enable_estimates}
                      onChange={handleToggleEnableEstimates}
                    />
                    <ToggleRow
                      label="Enable Invoice"
                      description="Allows the user to view and manage job invoices. This should be enabled only for users involved in billing or financial review. Example: an admin or account manager can open invoiced jobs, review charges, and help resolve billing questions from the customer."
                      enabled={userDetails.profile.enable_invoice}
                      onChange={handleToggleEnableInvoice}
                      border={false}
                    />
                  </>
                )}
              </div>
            </SectionCard>

            {(userDetails.is_staff ||
              userDetails.is_superuser ||
              userDetails.is_account_manager ||
              userDetails.is_internal_coordinator ||
              userDetails.is_customer_user) && (
              <>
                <SectionCard
                  title="Email Notifications"
                  description="Configure which email notifications the user should be getting."
                  icon={<BellIcon className="h-6 w-6" />}
                  open={showEmailNotificationSection}
                  onToggle={() =>
                    setShowEmailNotificationSection(
                      !showEmailNotificationSection,
                    )
                  }
                >
                  <div>
                    <ToggleRow
                      label="Enable Email Notifications"
                      description=""
                      enabled={userDetails.profile.email_notifications}
                      onChange={handleToggleEmailNotifications}
                    />

                    <ToggleRow
                      label="Job Submitted"
                      description="Notifies the user when a job is submitted by a customer user."
                      enabled={
                        userDetails.profile
                          .enable_email_notification_job_created
                      }
                      onChange={handleToggleEmailNotificationJobCreated}
                    />

                    {!userDetails.is_customer_user && (
                      <>
                        <ToggleRow
                          label="Scheduled Job Created"
                          description="Notifies the user when a job is created via an schedule."
                          enabled={
                            userDetails.profile
                              .enable_email_notification_scheduled_job_created
                          }
                          onChange={
                            handleToggleEmailNotificationScheduledJobCreated
                          }
                        />
                        <ToggleRow
                          label="Job Confirmed"
                          description="Notifies the user when a job is confirmed via a public shareable link."
                          enabled={
                            userDetails.profile
                              .enable_email_notification_job_confirmed
                          }
                          onChange={handleToggleEmailNotificationJobConfirmed}
                        />
                        <ToggleRow
                          label="Job Accepted"
                          description="Notifies the user when a job is accepted."
                          enabled={
                            userDetails.profile
                              .enable_email_notification_job_accepted
                          }
                          onChange={handleToggleEmailNotificationJobAccepted}
                        />
                        <ToggleRow
                          label="Job Returned"
                          description="Notifies the user when a job is return."
                          enabled={
                            userDetails.profile
                              .enable_email_notification_job_returned
                          }
                          onChange={handleToggleEmailNotificationJobReturned}
                        />
                        <ToggleRow
                          label="Job Comment Added"
                          description="Notifies the user when a comment is added to a job by a customer user."
                          enabled={
                            userDetails.profile
                              .enable_email_notification_job_comment_added
                          }
                          onChange={
                            handleToggleEmailNotificationJobCommentAdded
                          }
                        />
                      </>
                    )}

                    <ToggleRow
                      label="Job Completed"
                      description="Notifies the user when a job is completed."
                      enabled={
                        userDetails.profile
                          .enable_email_notification_job_completed
                      }
                      onChange={handleToggleEmailNotificationJobCompleted}
                    />

                    {!userDetails.is_customer_user && (
                      <>
                        <ToggleRow
                          label="Inventory Out of Stock"
                          description="Notifies the user when an inventory item goes out of stock. The inventory location must be configured to send notifications"
                          enabled={
                            userDetails.profile
                              .enable_email_notification_inventory_out_of_stock
                          }
                          onChange={
                            handleToggleEmailNotificationInventoryOutOfStock
                          }
                        />
                        <ToggleRow
                          label="Inventory Threshold Met"
                          description="Notifies the user when an inventory item reaches its threshold. The inventory location must be configured to send notifications"
                          enabled={
                            userDetails.profile
                              .enable_email_notification_inventory_threshold_met
                          }
                          onChange={handleToggleEmailInventoryThresholdMet}
                          border={false}
                        />
                      </>
                    )}
                  </div>
                </SectionCard>

                <SectionCard
                  title="SMS Notifications"
                  description="Configure which SMS notifications the user should be getting."
                  icon={<BellIcon className="h-6 w-6" />}
                  open={showSMSNotificationSection}
                  onToggle={() =>
                    setShowSMSNotificationSection(!showSMSNotificationSection)
                  }
                >
                  <div>
                    <ToggleRow
                      label="Enable SMS Notifications"
                      description=""
                      enabled={userDetails.profile.sms_notifications}
                      onChange={handleToggleSMSNotifications}
                    />

                    {!userDetails.is_customer_user && (
                      <>
                        <ToggleRow
                          label="Job Created"
                          description="Notifies the user when a job is created."
                          enabled={
                            userDetails.profile
                              .enable_sms_notification_job_created
                          }
                          onChange={handleToggleSMSNotificationJobCreated}
                        />
                        <ToggleRow
                          label="Job Started"
                          description="Notifies the user when a job is started."
                          enabled={
                            userDetails.profile
                              .enable_sms_notification_job_started
                          }
                          onChange={handleToggleSMSNotificationJobStarted}
                        />
                        <ToggleRow
                          label="Job Cancelled"
                          description="Notifies the user when a job is cancelled."
                          enabled={
                            userDetails.profile
                              .enable_sms_notification_job_cancelled
                          }
                          onChange={handleToggleSMSNotificationJobCancelled}
                        />
                      </>
                    )}

                    <ToggleRow
                      label="Job Completed"
                      description="Notifies the user when a job is completed."
                      enabled={
                        userDetails.profile
                          .enable_sms_notification_job_completed
                      }
                      onChange={handleToggleSMSNotificationJobCompleted}
                      border={false}
                    />
                  </div>
                </SectionCard>
              </>
            )}

            {(userDetails.is_project_manager ||
              userDetails.is_internal_coordinator) && (
              <>
                <SectionCard
                  title="Inventory Locations"
                  description="Manage inventory locations. This user will only be manage items for the locations specify here."
                  icon={<LocationMarkerIcon className="h-6 w-6" />}
                  open={showLocationSection}
                  onToggle={() => setShowLocationSection(!showLocationSection)}
                  badge={`${availableLocations.length} assigned`}
                >
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Add Location
                      </label>
                      <SearchableListbox
                        value={locationSelected}
                        onChange={setLocationSelected}
                        options={locations}
                        placeholder="Locations"
                        searchValue={locationSearchText}
                        onSearchChange={setLocationSearchText}
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => {
                          if (locationSelected?.id) {
                            addAvailableLocation(locationSelected.id);
                          }
                        }}
                        className="w-full rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 lg:w-auto"
                      >
                        Add Location
                      </button>
                    </div>
                  </div>

                  {locationAlreadyAdded ? (
                    <div className="mt-3 text-sm font-medium text-amber-600">
                      This location is already assigned to the user.
                    </div>
                  ) : null}

                  <div className="mt-5 space-y-3">
                    {availableLocations.length === 0 ? (
                      <EmptyState
                        title="No locations assigned"
                        description="Use the selector above to assign a location."
                      />
                    ) : (
                      availableLocations.map((location) => (
                        <div
                          key={location.id}
                          className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="text-sm font-medium text-slate-900">
                            {location.name || location.location_name}
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteAvailableLocation(location.id)}
                            className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </SectionCard>
                <SectionCard
                  title="Airports"
                  description="Manage airports. This user will only be able to see airports specified here."
                  icon={<OfficeBuildingIcon className="h-6 w-6" />}
                  open={showAirportSection}
                  onToggle={() => setShowAirportSection(!showAirportSection)}
                  badge={`${availableAirports.length} assigned`}
                >
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Add Airport
                      </label>
                      <SearchableListbox
                        value={airportSelected}
                        onChange={setAirportSelected}
                        options={airports}
                        placeholder="Airports"
                        searchValue={airportSearchText}
                        onSearchChange={setAirportSearchText}
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => {
                          if (airportSelected?.id) {
                            addAvailableAirport(airportSelected.id);
                          }
                        }}
                        className="w-full rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 lg:w-auto"
                      >
                        Add Airport
                      </button>
                    </div>
                  </div>

                  {airportAlreadyAdded ? (
                    <div className="mt-3 text-sm font-medium text-amber-600">
                      This airport is already assigned to the user.
                    </div>
                  ) : null}

                  <div className="mt-5 space-y-3">
                    {availableAirports.length === 0 ? (
                      <EmptyState
                        title="No airports assigned"
                        description="Use the selector above to assign an airport."
                      />
                    ) : (
                      availableAirports.map((airport) => (
                        <div
                          key={airport.id}
                          className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="text-sm font-medium text-slate-900">
                            {airport.name ||
                              airport.code ||
                              airport.airport_name}
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteAvailableAirport(airport.id)}
                            className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </SectionCard>
              </>
            )}

            {userDetails.is_internal_coordinator && (
              <>
                <SectionCard
                  title="Customers"
                  description="Manage customers. This user will only be able to see and create jobs for the specified customers. If no customers are specified, then the user will be able to see and create jobs for all customers."
                  icon={<OfficeBuildingIcon className="h-6 w-6" />}
                  open={showCustomerSection}
                  onToggle={() => setShowCustomerSection(!showCustomerSection)}
                  badge={`${availableCustomers.length} assigned`}
                >
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Add Customer
                      </label>
                      <SearchableListbox
                        value={customerSelected}
                        onChange={setCustomerSelected}
                        options={customers}
                        placeholder="Customers"
                        searchValue={customerSearchText}
                        onSearchChange={setCustomerSearchText}
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => {
                          if (customerSelected?.id) {
                            addUserCustomer(customerSelected.id);
                          }
                        }}
                        className="w-full rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 lg:w-auto"
                      >
                        Add Customer
                      </button>
                    </div>
                  </div>

                  {customerAlreadyAdded ? (
                    <div className="mt-3 text-sm font-medium text-amber-600">
                      This customer is already assigned to the user.
                    </div>
                  ) : null}

                  <div className="mt-5 space-y-3">
                    {availableCustomers.length === 0 ? (
                      <EmptyState
                        title="No customers assigned"
                        description="Use the selector above to assign a customer."
                      />
                    ) : (
                      availableCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="text-sm font-medium text-slate-900">
                            {customer.name}
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteUserCustomer(customer.id)}
                            className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </SectionCard>
              </>
            )}

            {userDetails.is_customer_user && (
              <SectionCard
                title="Customers"
                description="Manage customers. This user will only be able to see and create jobs for the specified customers. If no customers are specified, then the user will be able to see and create jobs for all customers."
                icon={<OfficeBuildingIcon className="h-6 w-6" />}
                open={showCustomerSection}
                onToggle={() => setShowCustomerSection(!showCustomerSection)}
                badge={`${availableCustomers.length} assigned`}
              >
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Add Customer
                    </label>
                    <SearchableListbox
                      value={customerSelected}
                      onChange={setCustomerSelected}
                      options={customers}
                      placeholder="Customers"
                      searchValue={customerSearchText}
                      onSearchChange={setCustomerSearchText}
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => {
                        if (customerSelected?.id) {
                          addUserCustomer(customerSelected.id);
                        }
                      }}
                      className="w-full rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 lg:w-auto"
                    >
                      Add Customer
                    </button>
                  </div>
                </div>

                {customerAlreadyAdded ? (
                  <div className="mt-3 text-sm font-medium text-amber-600">
                    This customer is already assigned to the user.
                  </div>
                ) : null}

                <div className="mt-5 space-y-3">
                  {availableCustomers.length === 0 ? (
                    <EmptyState
                      title="No customers assigned"
                      description="Use the selector above to assign a customer."
                    />
                  ) : (
                    availableCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="text-sm font-medium text-slate-900">
                          {customer.name}
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteUserCustomer(customer.id)}
                          className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </SectionCard>
            )}
          </div>
        </div>
      </div>
    </AnimatedPage>
  );
};

export default UserDetails;
